/**
 * One account's ordering rhythm, and the gap at the end of it.
 *
 * This is the whole reason a break is legible rather than asserted. A finding
 * says "not ordered in 119 days (locked every 7d)"; the chart shows fifty-two
 * weeks of orders arriving like clockwork and then stopping, which is the thing
 * a reader actually needs to judge whether it is real.
 *
 * What is drawn, and why each part earns its ink:
 *
 * * **A bar per order**, height by gallons. Order grain, never bucketed by
 *   week — a weekly bucket smooths over exactly the irregularity being judged.
 * * **The silence band**, from the last order to today. Its width is the
 *   finding.
 * * **Due marks** at each expected interval after the last order, so "three
 *   cycles missed" is something you count rather than take on trust.
 * * **The median order line**, so a volume break reads as bars sitting under it
 *   rather than as a number in a column.
 *
 * Nothing here is computed from the orders: the interval, the median and the
 * silence all arrive from the payload, where `derive` owns them.
 */

import { useElementWidth } from '../shared/useElementWidth'

export interface OrderPoint {
  readonly date: string
  readonly gallons: number
  /** Placed but not yet shipped. A2 counts these from the moment placed. */
  readonly isOpen: boolean
}

interface Props {
  readonly orders: readonly OrderPoint[]
  readonly lastOrder: string | null
  /** The agent's watermark. `daysSilent` is measured to here, not to today. */
  readonly measuredTo: string | null
  readonly expectedInterval: number | null
  readonly medianOrder: number | null
  readonly daysSilent: number | null
  /** From the payload, not from the marks drawn: a long break outruns the cap. */
  readonly cyclesMissed: number | null
  /** The cadence model has too little history to be trusted (see manifest). */
  readonly unproven: boolean
}

const HEIGHT = 200
const MARGIN = { top: 18, right: 16, bottom: 34, left: 52 }

/** Midnight UTC for a `YYYY-MM-DD` cell, so a timezone cannot shift a day. */
function day(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`)
}

const DAY_MS = 86_400_000
const MAX_DUE_MARKS = 30

function label(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function BreakTimeline({
  orders,
  lastOrder,
  measuredTo,
  expectedInterval,
  medianOrder,
  daysSilent,
  cyclesMissed,
  unproven,
}: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const chartWidth = Math.max(width || 640, 360)

  if (orders.length === 0) {
    return (
      <div ref={ref} className="state">
        No orders in the last 52 weeks for this account.
      </div>
    )
  }

  const plotWidth = chartWidth - MARGIN.left - MARGIN.right
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom

  const today = Date.now()
  const firstMs = day(orders[0]?.date ?? '')
  // Always run the axis to today: a break is the distance between the last bar
  // and the right-hand edge, so an axis stopping at the last order would draw
  // an account that never broke.
  const minMs = Math.min(firstMs, today - 364 * DAY_MS)
  const maxMs = today
  const span = Math.max(maxMs - minMs, DAY_MS)

  const peak = Math.max(...orders.map((o) => o.gallons), medianOrder ?? 0) * 1.15 || 1
  const x = (ms: number) => MARGIN.left + (plotWidth * (ms - minMs)) / span
  const y = (value: number) => MARGIN.top + plotHeight * (1 - value / peak)

  const lastMs = lastOrder === null ? null : day(lastOrder)
  const silenceStart = lastMs === null ? null : x(lastMs)
  // The break is measured to the agent's watermark, so the band closes there.
  // Running it to today would draw a longer silence than the finding claims,
  // and would hide an order placed after the agent last looked.
  const measuredMs = measuredTo === null ? null : day(measuredTo)
  const silenceEnd =
    measuredMs === null ? chartWidth - MARGIN.right : Math.min(x(measuredMs), chartWidth - MARGIN.right)

  // Where the next orders were due, one interval apart after the last one.
  // Capped so a long break on a short cadence does not draw a solid block; the
  // legend reports the real count, which comes from the payload.
  const dueMarks: number[] = []
  if (lastMs !== null && expectedInterval !== null && expectedInterval > 0) {
    for (let n = 1; n <= MAX_DUE_MARKS; n += 1) {
      const dueMs = lastMs + n * expectedInterval * DAY_MS
      if (dueMs > (measuredMs ?? maxMs)) break
      dueMarks.push(dueMs)
    }
  }

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => minMs + span * f)
  const barWidth = Math.max(2, Math.min(9, plotWidth / Math.max(orders.length * 2.2, 40)))

  return (
    <div ref={ref} className="bd-chart">
      <svg
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label={`Order history, ${String(orders.length)} orders, ${String(
          daysSilent ?? 0,
        )} days silent`}
      >
        {/* the silence — the finding itself */}
        {silenceStart !== null && (
          <rect
            x={silenceStart}
            y={MARGIN.top}
            width={Math.max(0, silenceEnd - silenceStart)}
            height={plotHeight}
            className="bd-silence"
          />
        )}

        {/* median order size */}
        {medianOrder !== null && medianOrder > 0 && (
          <>
            <line
              x1={MARGIN.left}
              x2={chartWidth - MARGIN.right}
              y1={y(medianOrder)}
              y2={y(medianOrder)}
              className="bd-median"
            />
            <text x={MARGIN.left - 8} y={y(medianOrder) + 4} className="bd-axis-label bd-anchor-end">
              {Math.round(medianOrder).toLocaleString('en-US')}
            </text>
          </>
        )}

        {/* when the next order was due */}
        {dueMarks.map((ms, index) => (
          <line
            key={ms}
            x1={x(ms)}
            x2={x(ms)}
            y1={MARGIN.top}
            y2={MARGIN.top + plotHeight}
            className={index === 0 ? 'bd-due bd-due-first' : 'bd-due'}
          />
        ))}

        {/* the orders */}
        {orders.map((order) => {
          const cx = x(day(order.date))
          const top = y(order.gallons)
          return (
            <rect
              key={`${order.date}-${String(order.gallons)}`}
              x={cx - barWidth / 2}
              y={top}
              width={barWidth}
              height={Math.max(1, MARGIN.top + plotHeight - top)}
              className={order.isOpen ? 'bd-order is-open' : 'bd-order'}
            >
              <title>
                {`${order.date} — ${Math.round(order.gallons).toLocaleString('en-US')} gal${
                  order.isOpen ? ' (open, not yet shipped)' : ''
                }`}
              </title>
            </rect>
          )
        })}

        {/* how far the agent looked */}
        {measuredMs !== null && (
          <>
            <line
              x1={x(measuredMs)}
              x2={x(measuredMs)}
              y1={MARGIN.top - 6}
              y2={MARGIN.top + plotHeight}
              className="bd-watermark"
            />
            <text x={x(measuredMs)} y={MARGIN.top - 9} className="bd-axis-label bd-anchor-middle">
              measured to
            </text>
          </>
        )}

        {/* baseline and axis */}
        <line
          x1={MARGIN.left}
          x2={chartWidth - MARGIN.right}
          y1={MARGIN.top + plotHeight}
          y2={MARGIN.top + plotHeight}
          className="bd-axis"
        />
        {ticks.map((ms) => (
          <text key={ms} x={x(ms)} y={HEIGHT - 12} className="bd-axis-label bd-anchor-middle">
            {label(ms)}
          </text>
        ))}
      </svg>

      <p className="bd-legend">
        <span className="bd-key bd-key-order" /> order
        {expectedInterval !== null && (
          <>
            <span className="bd-key bd-key-due" /> due every {expectedInterval.toFixed(0)} days
          </>
        )}
        {orders.some((o) => o.isOpen) && (
          <>
            <span className="bd-key bd-key-open" /> placed, not yet shipped
          </>
        )}
        <span className="bd-key bd-key-silence" /> silent{' '}
        {daysSilent === null ? '—' : `${String(Math.round(daysSilent))} days`}
        {cyclesMissed !== null && cyclesMissed > 0 && (
          <> · {Math.round(cyclesMissed).toLocaleString('en-US')} cycles missed</>
        )}
        {unproven && (
          <span className="bd-warn">
            {' '}
            · cadence unproven — under 3 orders in 26 weeks, so the expected interval is a guess
          </span>
        )}
      </p>
    </div>
  )
}
